/** Dashboard navigator. */

import { DashboardNavDrawer } from 'component/dashboard/dashboard-nav-drawer';
import {
    type BottomTabBarProps,
    type BottomTabNavigationEventMap,
    type BottomTabNavigationOptions,
    BottomTabView,
} from 'expo-router/js-tabs';
import {
    CommonActions,
    createNavigatorFactory,
    type DefaultNavigatorOptions,
    getLabel,
    type HeaderOptions,
    type NavigationProp,
    type NavigatorTypeBagBase,
    type ParamListBase,
    type StaticConfig,
    type TabActionHelpers,
    type TabNavigationState,
    TabRouter,
    type TabRouterOptions,
    type TypedNavigator,
    useNavigationBuilder,
    useTheme,
} from 'expo-router/react-navigation';

export type DashboardNavigationEventMap = BottomTabNavigationEventMap;

export type DashboardNavigationProp<
    ParamList extends ParamListBase,
    RouteName extends keyof ParamList = keyof ParamList,
    NavigatorID extends string | undefined = undefined,
> = NavigationProp<
    ParamList,
    RouteName,
    NavigatorID,
    TabNavigationState<ParamList>,
    DashboardNavigationOptions,
    DashboardNavigationEventMap
> &
    TabActionHelpers<ParamList>;

export type DashboardNavigationOptions = HeaderOptions &
    Pick<BottomTabNavigationOptions, 'header' | 'tabBarIcon' | 'tabBarLabel' | 'tabBarButtonTestID' | 'title'> & {
        tabBarVisible?: boolean;
    };

export type DashboardNavigationConfig = {
    title: string;
};

/** @public */
export type DashboardNavigatorProps = DefaultNavigatorOptions<
    ParamListBase,
    string | undefined,
    TabNavigationState<ParamListBase>,
    DashboardNavigationOptions,
    DashboardNavigationEventMap,
    DashboardNavigationProp<ParamListBase>
> &
    TabRouterOptions &
    DashboardNavigationConfig;

/**
 * The dashboard navigator renders a tab bar as a sidebar. It supports a `title`
 * prop to set the title that is shown in the tab bar.
 */
function DashboardNavigator({
    id,
    initialRouteName,
    backBehavior,
    title,
    UNSTABLE_routeNamesChangeBehavior,
    children,
    layout,
    screenListeners,
    screenOptions,
    screenLayout,
    UNSTABLE_router,
    ...rest
}: DashboardNavigatorProps) {
    const { state, descriptors, navigation, NavigationContent } = useNavigationBuilder<
        TabNavigationState<ParamListBase>,
        TabRouterOptions,
        TabActionHelpers<ParamListBase>,
        DashboardNavigationOptions,
        DashboardNavigationEventMap
    >(
        // eslint-disable-next-line @repo/psi/component-or-render-prop
        TabRouter,
        {
            id,
            initialRouteName,
            backBehavior,
            UNSTABLE_routeNamesChangeBehavior,
            children,
            layout,
            screenListeners,
            screenOptions,
            screenLayout,
            UNSTABLE_router,
        },
    );

    const focusedRoute = state.routes[state.index];
    const focusedDescriptor = descriptors[focusedRoute.key];
    const focusedOptions = focusedDescriptor.options;

    /* eslint-disable react/no-unstable-nested-components */
    return (
        <NavigationContent>
            <BottomTabView
                {...rest}
                descriptors={{
                    ...descriptors,
                    [focusedRoute.key]: {
                        ...focusedDescriptor,
                        options: {
                            ...focusedOptions,
                            tabBarPosition: 'left',
                        },
                    },
                }}
                navigation={navigation}
                state={state}
                tabBar={(props) => (focusedOptions.tabBarVisible === false ? null : <DashboardTabBar {...props} title={title} />)}
            />
        </NavigationContent>
    );
    /* eslint-enable react/no-unstable-nested-components */
}

export type DashboardTypeBag<
    ParamList extends ParamListBase = ParamListBase,
    NavigatorID extends string | undefined = string | undefined,
> = {
    ParamList: ParamList;
    NavigatorID: NavigatorID;
    State: TabNavigationState<ParamList>;
    ScreenOptions: DashboardNavigationOptions;
    EventMap: BottomTabNavigationEventMap;
    NavigationList: {
        [RouteName in keyof ParamList]: DashboardNavigationProp<ParamList, RouteName, NavigatorID>;
    };
    Navigator: typeof DashboardNavigator;
};

export function createDashboardNavigator<
    const ParamList extends ParamListBase,
    const NavigatorID extends string | undefined = string | undefined,
    const TypeBag extends NavigatorTypeBagBase = DashboardTypeBag<ParamList, NavigatorID>,
    const Config extends StaticConfig<TypeBag> = StaticConfig<TypeBag>,
>(config?: Config): TypedNavigator<TypeBag, Config> {
    return createNavigatorFactory(DashboardNavigator)(config);
}

type DashboardTabBarProps = BottomTabBarProps & {
    title: string;
};

/**
 * A tab bar for the dashboard.
 *
 * @privateRemarks
 * A custom tab bar is needed because `<BottomTabBar>` does not support enough
 * customizability out of the box to match the designs for this tab bar, which
 * feature a title and (not yet implemented) a collapsed state.
 */
function DashboardTabBar({ descriptors, navigation, state, title }: DashboardTabBarProps) {
    const { colors } = useTheme();

    const focusedRoute = state.routes[state.index];
    const focusedDescriptor = descriptors[focusedRoute.key];

    // Reproduce the tab press behavior of `<BottomTabBar>`.
    const onTabPress = (tabKey: string) => {
        const route = state.routes.find((r) => r.name === tabKey);
        if (!route) {
            return;
        }

        const focused = route === focusedRoute;

        const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
        });

        if (!focused && !event.defaultPrevented) {
            navigation.dispatch({
                ...CommonActions.navigate(tabKey),
                target: state.key,
            });
        }
    };

    return (
        <DashboardNavDrawer
            onTabPress={onTabPress}
            selectedTabKey={focusedDescriptor.route.name}
            tabs={Object.values(descriptors).map(({ options, route }) => ({
                key: route.name,
                text: getLabel(
                    {
                        label: typeof options.tabBarLabel === 'string' ? options.tabBarLabel : undefined,
                        title: options.title,
                    },
                    route.name,
                ),
                icon: () => {
                    const focused = route === focusedDescriptor.route;
                    const color = focused ? colors.primary : colors.text;
                    return options.tabBarIcon?.({
                        color,
                        focused: route === focusedDescriptor.route,
                        size: 20,
                    });
                },
                testID: options.tabBarButtonTestID,
            }))}
            titleText={title}
        />
    );
}
